import React, { useEffect, useState } from "react";
import { OneKlicksFileSDK } from "../../../SDK/OneKlicksFileSDK";
import { oneKlicks } from "../../../SDK/oneclick";
import { getuserdata } from "../../../services/apiService";
import { toast } from "react-toastify";
import ImageShow from "../../../Uitiles/ImageShow";

const Gallery = ({ galleryImages = [], setGalleryImages, videos = [], setVideos, newVideoUrl, setNewVideoUrl, isUploading, setIsUploading, galleryid }) => {
    // Parse galleryImages if it's a string, otherwise use as is
    const parseGalleryImages = (images) => {
        // Handle undefined or null
        if (!images) {
            return [];
        }
        if (typeof images === 'string') {
            try {
                const parsed = JSON.parse(images);
                return Array.isArray(parsed) ? parsed : [];
            } catch (e) {
                console.error("Error parsing galleryImages:", e);
                return [];
            }
        }
        return Array.isArray(images) ? images : [];
    };

    // Parse videos if it's a string, otherwise use as is
    const parseVideos = (videosData) => {
        if (typeof videosData === 'string') {
            try {
                const parsed = JSON.parse(videosData);
                // Filter out any non-object items and empty arrays
                return Array.isArray(parsed) ? parsed.filter(item =>
                    typeof item === 'object' && item !== null && !Array.isArray(item)
                ) : [];
            } catch (e) {
                console.error("Error parsing videos:", e);
                return [];
            }
        }
        return Array.isArray(videosData) ? videosData.filter(item =>
            typeof item === 'object' && item !== null && !Array.isArray(item)
        ) : [];
    };

    const galleryImagesArray = parseGalleryImages(galleryImages).map((img, index) => {
        // Ensure a stable, unique id for React keys and operations
        const stableId = img?.id ?? img?.fileId ?? img?.url ?? `idx-${index}`;
        return { ...img, id: stableId };
    });
    const videosArray = parseVideos(videos);

    const handleImageUpload = async (event) => {
        const files = event.target.files;
        setIsUploading(true);
        try {
            const updatedGalleryImages = [...galleryImagesArray];
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const root = "doctors/gallery";
                const status = "public";
                try {
                    const fileId = await OneKlicksFileSDK.uploadFile(root, status, file);
                    if (fileId) {
                        const imageurl = await OneKlicksFileSDK.getfile(fileId.fileId);
                        const newImage = {
                            id: Date.now() + i,
                            name: file.name,
                            url: `${fileId.file.root}/${fileId.file.fileName}`,
                            fileId: fileId.fileId,
                            size: file.size
                        };
                        updatedGalleryImages.push(newImage);
                    } else {
                        console.error("Invalid fileId response:", fileId);
                    }
                } catch (uploadError) {
                    console.error(`Error uploading file ${file.name}:`, uploadError);
                }
            }

            // Update database with new images
            const tableSchema = "";
            const tableName = "hospitals_gallery";
            const data = {
                images: JSON.stringify(updatedGalleryImages)
            };
            const condition = {
                user_id: await getuserdata("id")
            };

            const updateResult = await oneKlicks.update(tableSchema, tableName, data, condition);
            if (!updateResult.success) {
                throw new Error(updateResult.error || "Failed to update gallery images");
            }

            setGalleryImages(updatedGalleryImages);
        } catch (error) {
            console.error("Error in handleImageUpload:", error);
        } finally {
            setIsUploading(false);
        }
    };

    const handleImageDelete = async (imageId) => {
        const imageToDelete = galleryImagesArray.find(i => i.id === imageId);
        if (!imageToDelete) return;

        // Optimistic update to minimize reconciliation issues
        const updatedImages = galleryImagesArray.filter(i => i.id !== imageId);
        const previousImages = galleryImagesArray;
        setGalleryImages(updatedImages);

        try {
            const user_id = await getuserdata("id");
            const tableSchema = "";
            const tableName = "hospitals_gallery";
            const data = {
                images: JSON.stringify(updatedImages)
            };
            const condition = {
                user_id: user_id
            };
            const updatedRegistrations = await oneKlicks.update(tableSchema, tableName, data, condition);
            if (!updatedRegistrations.success) {
                throw new Error(updatedRegistrations.error || "Failed to delete image");
            } else {
                toast.success("Image deleted successfully");
            }
        } catch (error) {
            console.error("Error deleting image:", error);
            // Rollback on failure
            setGalleryImages(previousImages);
        }
    };

    const getYouTubeEmbedUrl = (url) => {
        try {
            // Handle youtu.be short URLs
            if (url.includes('youtu.be')) {
                const videoId = url.split('youtu.be/')[1].split('?')[0];
                return `https://www.youtube.com/embed/${videoId}`;
            }
            // Handle youtube.com URLs
            if (url.includes('youtube.com')) {
                const videoId = url.split('v=')[1]?.split('&')[0];
                if (videoId) {
                    return `https://www.youtube.com/embed/${videoId}`;
                }
            }
            return url;
        } catch (error) {
            console.error('Error parsing YouTube URL:', error);
            return url;
        }
    };

    const handleVideoAdd = async () => {
        if (newVideoUrl) {
            console.log("NEW VIDEO URL", newVideoUrl);
            console.log("VIDEOS", videosArray);

            // Check if it's a YouTube URL
            const isYouTubeUrl = newVideoUrl.includes('youtube.com') || newVideoUrl.includes('youtu.be');

            if (isYouTubeUrl) {
                const embedUrl = getYouTubeEmbedUrl(newVideoUrl);
                const updatedVideos = [...videosArray, {
                    id: Date.now(),
                    name: embedUrl,
                    type: 'youtube'
                }];

                // Update database with new videos
                const tableSchema = "";
                const tableName = "hospitals_gallery";
                const data = {
                    videos: JSON.stringify(updatedVideos)
                };
                const condition = {
                    user_id: await getuserdata("id")
                };

                const updateResult = await oneKlicks.update(tableSchema, tableName, data, condition);
                if (!updateResult.success) {
                    throw new Error(updateResult.error || "Failed to update videos");
                }

                setVideos(updatedVideos);
            } else {
                // Handle local video file
                const file = newVideoUrl;
                if (file) {
                    setIsUploading(true);
                    try {
                        const root = "doctors/gallery";
                        const status = "public";
                        const fileId = await OneKlicksFileSDK.uploadFile(root, status, file);
                        if (fileId) {
                            const videoUrl = await OneKlicksFileSDK.getfile(fileId.fileId);
                            const updatedVideos = [...videosArray, {
                                id: Date.now(),
                                name: file.name,
                                type: 'local',
                                url: videoUrl.fileUrl,
                                fileId: fileId.fileId,
                                size: file.size
                            }];

                            // Update database with new videos
                            const tableSchema = "";
                            const tableName = "hospitals_gallery";
                            const data = {
                                videos: JSON.stringify(updatedVideos)
                            };
                            const condition = {
                                user_id: await getuserdata("id")
                            };

                            const updateResult = await oneKlicks.update(tableSchema, tableName, data, condition);
                            if (!updateResult.success) {
                                throw new Error(updateResult.error || "Failed to update videos");
                            }

                            setVideos(updatedVideos);
                        } else {
                            console.error("Invalid fileId response for video:", fileId);
                        }
                    } catch (error) {
                        console.error("Error uploading video:", error);
                    } finally {
                        setIsUploading(false);
                    }
                }
            }
            setNewVideoUrl("");
        }
    };

    const handleVideoDelete = async (videoId) => {
        const videoToDelete = videosArray.find(v => v.id === videoId);
        if (videoToDelete) {
            try {
                const updatedVideos = videosArray.filter(video => video.id !== videoId);

                // Update database with new videos
                const tableSchema = "";
                const tableName = "hospitals_gallery";
                const data = {
                    videos: JSON.stringify(updatedVideos)
                };
                const condition = {
                    user_id: await getuserdata("id")
                };

                const updateResult = await oneKlicks.update(tableSchema, tableName, data, condition);
                if (!updateResult.success) {
                    throw new Error(updateResult.error || "Failed to delete video");
                }

            } catch (error) {
                console.error("Error deleting video:", error);
            }
        }
    };

    const handleVideoFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            setNewVideoUrl(file);
        }
    };

    return (
        <div className="dc-tabscontent tab-content">
            <div className="dc-personalskillshold tab-pane active fade show" id="dc-skills">
                <div className="dc-profilephoto dc-tabsinfo">
                    <div className="dc-tabscontenttitle">
                        <h3>Hospital Gallery</h3>
                    </div>
                    <div className="dc-profilephotocontent">
                        <div className="dc-formtheme dc-formprojectinfo dc-formcategory">
                            <fieldset>
                                <div className="form-group form-group-label">
                                    <div className="dc-labelgroup">
                                        <label htmlFor="file" className="dc-image-file">
                                            <span className="ajax-load dc-btn">Select File</span>
                                        </label>
                                        <span>Drop files here to upload</span>
                                        {isUploading && (
                                            <em className="dc-fileuploading">
                                                Uploading<i className="fa fa-spinner fa-spin"></i>
                                            </em>
                                        )}
                                    </div>
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/jpeg,.jpg,.jpeg,image/gif,.gif,image/png,.png"
                                        onChange={handleImageUpload}
                                        style={{ display: 'none' }}
                                        id="file"
                                    />
                                </div>
                                <div className="form-group uploaded-placeholder">
                                    <ul className="dc-attachfile dc-attachfilevtwo dc-galler-images">
                                        {galleryImagesArray.map((image) => (
                                            <li key={image.id} className="dc-uploadingholder dc-companyimg-user">
                                                <div className="dc-uploadingbox">
                                                    <figure>
                                                        <ImageShow className="img-thumb" url={image.url} alt={image.name} />
                                                    </figure>
                                                    <div className="dc-uploadingbar">
                                                        <span className="uploadprogressbar"></span>
                                                        <span>{image.name}</span>
                                                        <em>
                                                            File size: {image.size}
                                                            <button type="button" className="dc-remove-gallery-image lnr lnr-cross" onClick={() => handleImageDelete(image.id)}></button>
                                                        </em>
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </fieldset>
                        </div>
                    </div>
                </div>

                <div className="dc-skills dc-tabsinfo dc-tabsinfo-mt">
                    <div className="dc-tabscontenttitle">
                        <h3>Videos</h3>
                    </div>
                    <div className="dc-skillscontent-holder">
                        <div className="dc-formtheme dc-skillsform">
                            <fieldset>
                                <div className="form-group">
                                    <div className="form-group-holder">
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="input_membership"
                                            placeholder="Enter YouTube URL or select local video"
                                            value={newVideoUrl}
                                            onChange={(e) => setNewVideoUrl(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <div className="form-group-holder">
                                        <input
                                            type="file"
                                            accept="video/*"
                                            onChange={handleVideoFileUpload}
                                            className="form-control"
                                        />
                                    </div>
                                </div>
                                <div className="form-group dc-btnarea">
                                    <a href="#" className="dc-btn dc-add_memberships" onClick={(e) => {
                                        e.preventDefault();
                                        handleVideoAdd();
                                    }}>
                                        Add Now
                                    </a>
                                </div>
                            </fieldset>
                        </div>
                        <div className="dc-myskills">
                            <ul className="sortable list dc-memberships dc-sortable-list">
                                {videosArray.map((video) => (
                                    <li key={video.id}>
                                        <div className="dc-dragdroptool">
                                            <a href="javascript:" className="lnr lnr-menu"></a>
                                        </div>
                                        <span className="skill-dynamic-html">
                                            <em className="skill-val">
                                                {video.type === 'youtube' ? (
                                                    <div className="youtube-video-container">
                                                        <iframe
                                                            width="280"
                                                            height="157"
                                                            src={video.name}
                                                            title="YouTube video player"
                                                            frameBorder="0"
                                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                            allowFullScreen
                                                            onError={(e) => {
                                                                console.error('Error loading YouTube video:', e);
                                                                e.target.style.display = 'none';
                                                                e.target.parentElement.innerHTML = `
                                                                    <div class="video-error">
                                                                        <p>Video unavailable</p>
                                                                        <a href="${video.name}" target="_blank" rel="noopener noreferrer">
                                                                            Open in YouTube
                                                                        </a>
                                                                    </div>
                                                                `;
                                                            }}
                                                        ></iframe>
                                                    </div>
                                                ) : (
                                                    <div className="local-video-container">
                                                        <video
                                                            controls
                                                            width="280"
                                                            height="157"
                                                            src={video.url}
                                                            className="local-video-player"
                                                        >
                                                            Your browser does not support the video tag.
                                                        </video>
                                                    </div>
                                                )}
                                            </em>
                                        </span>
                                        <div className="dc-rightarea">
                                            <a href="#" className="dc-deleteinfo" onClick={(e) => {
                                                e.preventDefault();
                                                handleVideoDelete(video.id);
                                            }}>
                                                <i className="lnr lnr-trash"></i>
                                            </a>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Gallery;
